import React, { useCallback, useEffect, useMemo } from 'react';

// DEPENDENCIES
import clsx from 'clsx';
import { Field, useFormikContext } from 'formik';
import * as yup from 'yup';

// GLOBAL VARIABLES
import { ENDPOINTS } from 'endpoints.js';
import { SALES_FILE_TYPE_IDS } from 'globals.js';

// GLOBAL FUNCTIONS
import { makePath } from 'functions.js';

// HOOKS
import useRecordView from 'helpers/getRecordView';
import { useRecordContext } from 'helpers/getRecordData';

// CORE COMPONENTS
import UploadButton from 'core/form/control/types/upload/parts/UploadButton';

// LOCAL COMPONENTS
import { WARNING_MESSAGE } from 'routers/interface/tools/attachment/parts/AttachmentUpload';

// STYLES
import styles from './attachmentButton.module.scss';

// MAIN COMPONENT
const AttachmentButton = ({
  index,
  className,
  label,
  icon,
  link,
  outline,
  fullWidth,
  item
}) => {

  // FORMIK
  const {
    values: { arySpotInstruction = [], sale_type_category_id },
    initialValues,
    setFieldValue,
    validateField
  } = useFormikContext();

  // HOOKS
  const { isAdd, isView } = useRecordView();
  const { record, recordID } = useRecordContext() || {};

  // MEMOS
  const spotInstructionId = useMemo(
    () => {
      const spotInstruction = arySpotInstruction?.[index] || [];
      return spotInstruction?.automated_spot_instruction_id || spotInstruction?.spot_instruction_id;
    },
    [index, arySpotInstruction]
  );

  const isRequired = +item?.upload_document_required === 1;

  // EFFECTS
  useEffect(() => {
    validateField(`arySpotInstruction[${index}].arySpotInstructionAttachment`);
  }, [arySpotInstruction, index, validateField]);

  const validationSchema = isRequired
    ? yup.array().min(1, 'Attachment is required').required('Attachment is required')
    : yup.mixed().notRequired();

  // CALLBACKS
  const validate = useCallback(async (value) => {
    try {
      await validationSchema.validate(value);
      return undefined;
    } catch (error) {
      return error.message;
    }
  }, [validationSchema]);
  
  const handleUpload = useCallback(
    () => {
      if (!isAdd && spotInstructionId !== '') setFieldValue(`arySpotInstruction[${index}].isUpdated`, true);
    },
    [isAdd, spotInstructionId, setFieldValue, index]
  );

  const handleUploadMeta = useCallback(data => ({
    temp: !!data.temp_attachment_id,
    attachmentId: data.salesattachment_id || data.temp_attachment_id || data.attachmentId,
    fileName: data.salesattachment_filename || data.fileName,
    uploadedBy: data.updated_by || data.created_by || data.uploadedBy,
    uploadedOn: data.date_updated || data.date_created || data.uploadedOn || data.timecreated
  }), []);

  // If it's a newly added Spot Instruction then okay to use temp endpoint
  const isNewSpotInstruction = !initialValues?.arySpotInstruction?.[index];

  const documentTypes = {
    0: {
      1: SALES_FILE_TYPE_IDS.SPOT_INSTRUCTION_RETAIL_USED,
      2: SALES_FILE_TYPE_IDS.SPOT_INSTRUCTION_WHOLESALE_USED
    },
    1: {
      1: SALES_FILE_TYPE_IDS.SPOT_INSTRUCTION_RETAIL_NEW,
      2: SALES_FILE_TYPE_IDS.SPOT_INSTRUCTION_WHOLESALE_NEW
    }
  };
  const newUsed = record?.new_used ?? record?.inventory?.new_used;
  const documentType = documentTypes?.[+newUsed]?.[+sale_type_category_id];

  // RENDER
  return (
    <>
      <div className="d-flex align-items-center">
        {isRequired && (
          <div className={clsx(styles.required, 'px-2 py-1 me-2 text-primary text-nowrap')}>
            <strong>Required *</strong>
          </div>
        )}
        <UploadButton
          label="Spot Instruction Attachment"
          uploadArgs={(_uploadID, file) => ({
            endpoint: !isView && isNewSpotInstruction
              ? ENDPOINTS.uploads.sales.temp
              : makePath(ENDPOINTS.uploads.sales.upload, record.sales_id),
            params: isAdd ? {
              dailySalesInventoryId: record.daily_sales_inventory_id,
              documentType,
              fileName: file.name
            } : {
              documentType,
              spotInstructionId,
              fileName: file.name,
              salesId: recordID
            }
          })}
          uploadMeta={handleUploadMeta}
          uploadFieldValue={data => ({
            temp_attachment_id: data.temp_attachment_id,
            salesattachment_id:  data.salesattachment_id || data.attachmentId,
            salesattachment_filename: data.fileName,
            created_by_id: data.created_by_site_user_id,
            created_by: data.uploadedBy,
            date_created: data.timecreated,
            spot_instruction_id: spotInstructionId
          })}
          multiple
          onUpload={handleUpload}
          button={{
            className,
            label,
            icon,
            link,
            outline,
            fullWidth
          }}
          modal={{
            message: WARNING_MESSAGE
          }}
        />
      </div>
      <Field
        name={`arySpotInstruction[${index}].arySpotInstructionAttachment`}
        validate={validate}
        value={arySpotInstruction?.[index]?.arySpotInstructionAttachment}
        className="d-none"
      />
    </>
  )
}

export default AttachmentButton;
